// @flow
import {type AxiosPromise} from 'axios';
import {FULFILLED, PENDING} from 'redux-promise-middleware';
import {createActions, handleActions, type ReduxReducer} from 'redux-actions';

import type {ActionType} from 'constants/flow/flowTypes';
import {CART} from 'constants/actions';
import {ENDPOINTS} from 'constants/endpoints';
import type {AddToCartOldType} from 'reducers/product/discountPrice/discountPriceType';
import {instanceGusaBackend} from 'libs/api';

import type {
    BuyNowPopupPriceResponseType,
    BuyNowPopupToggleActionPayloadType,
    BuyNowPopupType,
} from './buyNowPopupType';

export const defaultState = {
    isVisible: false,
    isRx: false,
    status: 'pending',
    total: 0,
};

export const {
    checkoutBuyNowPopup: {toggle, fetchProductPrice},
} = createActions({
    CHECKOUT_BUY_NOW_POPUP: {
        /**
         * Toggles buy now popup
         * @param {boolean} isVisible - visibility state
         * @param {boolean} [isRx=false] - is rx state
         * @return {BuyNowPopupToggleActionPayloadType} action payload
         */
        TOGGLE: (
            isVisible: boolean,
            isRx?: boolean = false,
        ): BuyNowPopupToggleActionPayloadType => ({isVisible, isRx}),

        /**
         * Sends product price request
         * @param {number} productId - product identifier
         * @param {AddToCartOldType} data - product data
         * @return {AxiosPromise<BuyNowPopupPriceResponseType>} response promise
         */
        FETCH_PRODUCT_PRICE: (
            productId: number,
            data: AddToCartOldType,
        ): AxiosPromise<BuyNowPopupPriceResponseType> =>
            instanceGusaBackend({
                method: 'get',
                url: ENDPOINTS.PRODUCT_GET_DISCOUNT_PRICE({productId}),
                params: {...data, useCurrentQuote: 1},
            }),
    },
});

export const quickCheckoutBuyNowPopup: ReduxReducer<
    BuyNowPopupType,
    ActionType<boolean | AxiosPromise>,
> = handleActions(
    {
        /**
         * Toggle action reducer resolver
         * @param {BuyNowPopupType} state - current reducer state
         * @param {ActionType<BuyNowPopupToggleActionPayloadType>} action - dispatched action
         * @returns {BuyNowPopupType} new reducer state
         */
        [toggle]: (
            state: BuyNowPopupType,
            {payload}: ActionType<BuyNowPopupToggleActionPayloadType>,
        ): BuyNowPopupType => ({
            ...state,
            isVisible: payload.isVisible,
            isRx: payload.isRx,
        }),

        /**
         * Product price pending action reducer resolver
         * @param {BuyNowPopupType} state - current reducer state
         * @return {BuyNowPopupType} new reducer state
         */
        [`${fetchProductPrice}_${PENDING}`]: (state: BuyNowPopupType): BuyNowPopupType => ({
            ...state,
            status: 'pending',
        }),

        /**
         * Closes the popup after successfully adding an item to cart
         * @param {BuyNowPopupType} state - current reducer state
         * @returns {BuyNowPopupType} new reducer state
         */
        [`${CART.ADD_TO_CART}_${FULFILLED}`]: (state: BuyNowPopupType): BuyNowPopupType => ({
            ...state,
            isVisible: false,
        }),

        /**
         * Product price fulfilled action reducer resolver
         * @param {BuyNowPopupType} state - current reducer state
         * @param {ActionType<AxiosPromise<BuyNowPopupPriceResponseType>>} action - dispatched action
         * @return {BuyNowPopupType} new reducer state
         */
        [`${fetchProductPrice}_${FULFILLED}`]: (
            state: BuyNowPopupType,
            {payload}: ActionType<AxiosPromise<BuyNowPopupPriceResponseType>>,
        ): BuyNowPopupType => ({
            ...state,
            status: 'fulfilled',
            total: payload.data.finalPrice,
        }),
    },
    defaultState,
);
